/**
 * Operator portal rework PR 6 (migration 0212) — an operator company's plan, on the operator
 * page under the portal card: its template, its user limit, each feature's state with Paelode's
 * overrides and trials, and the history. The operator twin of tenants/PlanFeaturesTab.tsx,
 * smaller: operator templates have no EXPRESS-style customer and no data-capture features.
 *
 * Wording is Paelode-internal (template names are fine here; the operator never sees them).
 */
import * as React from 'react';
import { toast } from 'sonner';
import {
  OPERATOR_TEMPLATES,
  type OperatorFeatureView,
  type OperatorTemplate,
} from '@lodex-pro/shared/features';
import { Button } from 'src/components/ui/button';
import { FormRow, fieldStyle, labelStyle, subText } from '../../ui/bits';
import {
  OPERATOR_TEMPLATE_LABELS,
  historySummary,
  sourceLabel,
  stateLabel,
  trialEndIso,
} from '../tenants/plan-labels';
import {
  useApplyOperatorTemplate,
  useOperatorPlan,
  useResetOperatorFeature,
  useSetOperatorFeature,
  useSetOperatorSeatLimit,
  useStoreOperatorPlan,
} from './plan-hooks';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-card)',
  padding: '20px',
  marginBottom: '16px',
};
const rowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
  alignItems: 'flex-end',
  flexWrap: 'wrap',
  marginBottom: '12px',
};
const cell: React.CSSProperties = {
  padding: '6px',
  borderTop: '1px solid var(--color-border-primary)',
};

function failed(): void {
  toast.error('That did not work. Check the reason and values, and try again.');
}

/** "On · 30" / "On · no limit" for a limited feature; "On" / "Off" otherwise. */
function effectiveLabel(f: OperatorFeatureView): string {
  if (f.hasLimit && f.effective.enabled && f.effective.limit === null) return 'On · no limit';
  return stateLabel(f.effective.enabled, f.effective.limit);
}

function TemplateForm({
  tenantId,
  current,
}: {
  tenantId: string;
  current: OperatorTemplate;
}): React.JSX.Element {
  const [template, setTemplate] = React.useState<OperatorTemplate>(current);
  const [reason, setReason] = React.useState('');
  const [clearOverrides, setClearOverrides] = React.useState(false);
  const apply = useApplyOperatorTemplate(tenantId);
  const store = useStoreOperatorPlan(tenantId);
  return (
    <div style={rowStyle}>
      <FormRow label="Plan" htmlFor="op-plan-template">
        <select
          id="op-plan-template"
          value={template}
          onChange={(e): void => setTemplate(e.target.value as OperatorTemplate)}
          style={fieldStyle}
        >
          {OPERATOR_TEMPLATES.map((t) => (
            <option key={t} value={t}>
              {OPERATOR_TEMPLATE_LABELS[t]}
            </option>
          ))}
        </select>
      </FormRow>
      <FormRow label="Reason" htmlFor="op-plan-reason">
        <input
          className="keep-case"
          id="op-plan-reason"
          value={reason}
          onChange={(e): void => setReason(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <label style={{ ...labelStyle, display: 'flex', gap: '6px', alignItems: 'center' }}>
        <input
          type="checkbox"
          checked={clearOverrides}
          onChange={(e): void => setClearOverrides(e.target.checked)}
        />
        Clear overrides
      </label>
      <Button
        type="button"
        disabled={apply.isPending || reason.trim() === '' || template === current}
        onClick={(): void =>
          apply.mutate(
            { template, reason: reason.trim(), clearOverrides },
            {
              onSuccess: (res) => {
                store(res);
                setReason('');
                toast.success(`Plan set to ${OPERATOR_TEMPLATE_LABELS[template]}`);
              },
              onError: failed,
            },
          )
        }
      >
        Apply plan
      </Button>
    </div>
  );
}

function SeatLimitForm({
  tenantId,
  limit,
  active,
}: {
  tenantId: string;
  limit: number | null;
  active: number;
}): React.JSX.Element {
  const [value, setValue] = React.useState(limit === null ? '' : String(limit));
  const [reason, setReason] = React.useState('');
  const save = useSetOperatorSeatLimit(tenantId);
  const store = useStoreOperatorPlan(tenantId);
  const parsed = value.trim() === '' ? null : Number(value);
  const valid = parsed === null || (Number.isInteger(parsed) && parsed >= 0);
  return (
    <div style={rowStyle}>
      <FormRow label="User limit (blank = none)" htmlFor="op-plan-seats">
        <input
          id="op-plan-seats"
          inputMode="numeric"
          value={value}
          onChange={(e): void => setValue(e.target.value)}
          style={{ ...fieldStyle, width: '120px' }}
        />
      </FormRow>
      <FormRow label="Reason" htmlFor="op-plan-seats-reason">
        <input
          className="keep-case"
          id="op-plan-seats-reason"
          value={reason}
          onChange={(e): void => setReason(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <Button
        type="button"
        variant="outline"
        disabled={save.isPending || !valid || reason.trim() === '' || parsed === limit}
        onClick={(): void =>
          save.mutate(
            { userSeatLimit: parsed, reason: reason.trim() },
            {
              onSuccess: (res) => {
                store(res);
                setReason('');
                toast.success('User limit saved');
              },
              onError: failed,
            },
          )
        }
      >
        Save limit
      </Button>
      <span style={{ ...subText, textTransform: 'none' }}>
        {limit === null
          ? `${String(active)} active users, no limit`
          : `${String(active)} of ${String(limit)} users`}
      </span>
    </div>
  );
}

function FeatureEditor({
  tenantId,
  feature,
  timezone,
  onDone,
}: {
  tenantId: string;
  feature: OperatorFeatureView;
  timezone: string;
  onDone: () => void;
}): React.JSX.Element {
  const [enabled, setEnabled] = React.useState(feature.effective.enabled);
  const [limit, setLimit] = React.useState(
    feature.effective.limit === null ? '' : String(feature.effective.limit),
  );
  const [until, setUntil] = React.useState('');
  const [reason, setReason] = React.useState('');
  const set = useSetOperatorFeature(tenantId, feature.id);
  const reset = useResetOperatorFeature(tenantId, feature.id);
  const store = useStoreOperatorPlan(tenantId);
  const parsedLimit = !feature.hasLimit || !enabled || limit.trim() === '' ? null : Number(limit);
  const valid =
    reason.trim() !== '' &&
    (parsedLimit === null || (Number.isInteger(parsedLimit) && parsedLimit >= 0));
  const busy = set.isPending || reset.isPending;
  return (
    <div style={{ ...rowStyle, padding: '8px 0' }}>
      <label style={{ ...labelStyle, display: 'flex', gap: '6px', alignItems: 'center' }}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e): void => setEnabled(e.target.checked)}
        />
        On
      </label>
      {feature.hasLimit && enabled && (
        <FormRow label="Limit (blank = none)" htmlFor={`op-limit-${feature.id}`}>
          <input
            id={`op-limit-${feature.id}`}
            inputMode="numeric"
            value={limit}
            onChange={(e): void => setLimit(e.target.value)}
            style={{ ...fieldStyle, width: '100px' }}
          />
        </FormRow>
      )}
      <FormRow label="Trial until (optional)" htmlFor={`op-until-${feature.id}`}>
        <input
          id={`op-until-${feature.id}`}
          type="date"
          value={until}
          onChange={(e): void => setUntil(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <FormRow label="Reason" htmlFor={`op-reason-${feature.id}`}>
        <input
          className="keep-case"
          id={`op-reason-${feature.id}`}
          value={reason}
          onChange={(e): void => setReason(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <Button
        type="button"
        disabled={busy || !valid}
        onClick={(): void =>
          set.mutate(
            {
              enabled,
              limit: parsedLimit,
              expiresAt: trialEndIso(until, timezone),
              reason: reason.trim(),
            },
            {
              onSuccess: (res) => {
                store(res);
                toast.success(`${feature.name} saved`);
                onDone();
              },
              onError: failed,
            },
          )
        }
      >
        Save
      </Button>
      {feature.override !== null && (
        <Button
          type="button"
          variant="outline"
          disabled={busy || reason.trim() === ''}
          onClick={(): void =>
            reset.mutate(
              { reason: reason.trim() },
              {
                onSuccess: (res) => {
                  store(res);
                  toast.success(`${feature.name} back to the plan`);
                  onDone();
                },
                onError: failed,
              },
            )
          }
        >
          Reset to plan
        </Button>
      )}
      <Button type="button" variant="outline" onClick={onDone}>
        Cancel
      </Button>
    </div>
  );
}

export function OperatorPlanCard({ tenantId }: { tenantId: string }): React.JSX.Element {
  const { data, isLoading, isError } = useOperatorPlan(tenantId);
  const [editing, setEditing] = React.useState<string | null>(null);
  const plan = data?.plan;
  return (
    <section style={cardStyle} data-testid="operator-plan-card">
      <h3 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 14px' }}>Plan</h3>
      {isLoading ? (
        <p style={subText}>Loading…</p>
      ) : isError || plan === undefined ? (
        <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)' }}>
          Could not load the plan.
        </p>
      ) : (
        <>
          <TemplateForm key={`t-${plan.template}`} tenantId={tenantId} current={plan.template} />
          <SeatLimitForm
            key={`s-${String(plan.userSeatLimit)}`}
            tenantId={tenantId}
            limit={plan.userSeatLimit}
            active={plan.activeUsers}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th style={{ ...labelStyle, padding: '6px' }}>Feature</th>
                <th style={{ ...labelStyle, padding: '6px' }}>Now</th>
                <th style={{ ...labelStyle, padding: '6px' }}>From</th>
                <th style={{ ...labelStyle, padding: '6px' }} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {plan.features.map((f) => (
                <React.Fragment key={f.id}>
                  <tr>
                    <td style={cell} title={f.description}>
                      {f.name}
                    </td>
                    <td style={cell}>{effectiveLabel(f)}</td>
                    <td style={cell}>{sourceLabel(f, plan.timezone)}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={(): void => setEditing(editing === f.id ? null : f.id)}
                      >
                        Change
                      </Button>
                    </td>
                  </tr>
                  {editing === f.id && (
                    <tr>
                      <td colSpan={4}>
                        <FeatureEditor
                          tenantId={tenantId}
                          feature={f}
                          timezone={plan.timezone}
                          onDone={(): void => setEditing(null)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {plan.history.length > 0 && (
            <div style={{ marginTop: '14px' }}>
              <div style={labelStyle}>History</div>
              <ul style={{ margin: '6px 0 0', paddingLeft: '18px', fontSize: '13px' }}>
                {plan.history.slice(0, 10).map((h, i) => (
                  <li key={`${h.at}-${String(i)}`}>
                    {historySummary(h, plan.timezone)}
                    {h.byName !== null ? ` · ${h.byName}` : ''}
                    {h.reason !== null ? ` · “${h.reason}”` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}
